import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";

import {
  EMPTY_FILTERS,
  fetchArchived,
  fetchBoardFacets,
  fetchBoardPage,
  fetchSubtaskDescription,
  fetchTaskDetail,
  type BoardFilters,
  type BoardPage,
  type SignoffStatus,
} from "./board";
import { fetchNotifications, fetchUnreadCount } from "./notifications";
import { fetchSession, type SessionUser } from "./session";
import {
  fetchArchivePreview,
  fetchSettings,
  fetchSpaces,
  fetchWorkspace,
} from "./settings";
import { fetchMembers, fetchUsers } from "./users";

export const BOARD_PAGE_SIZE = 20;
export const NOTIFICATIONS_PAGE_SIZE = 25;
export const ARCHIVED_PAGE_SIZE = 25;

/**
 * Query-key registry. Centralising keys keeps `useQuery` and the matching
 * `invalidateQueries` in lockstep — invalidating `qk.board` busts every column.
 */
export const qk = {
  session: ["session"] as const,
  workspace: ["workspace"] as const,
  spaces: ["spaces"] as const,
  settings: ["settings"] as const,
  users: ["users"] as const,
  members: ["members"] as const,
  archivePreview: ["archive-preview"] as const,
  board: ["board"] as const,
  boardFacets: ["board-facets"] as const,
  archived: ["archived"] as const,
  task: ["task"] as const,
  subtaskDesc: ["subtask-desc"] as const,
  notifications: ["notifications"] as const,
  // NOT a child of `notifications` — keep it independent so invalidating the
  // feed/preview doesn't ambiguously also churn the unread badge (and vice
  // versa); both are updated explicitly where needed.
  unread: ["notifications-unread"] as const,
};

// ---- Session / workspace ----------------------------------------------------

export const sessionQueryOptions = queryOptions({
  queryKey: qk.session,
  queryFn: fetchSession,
  // Session changes only on login/logout, both of which invalidate explicitly.
  staleTime: Infinity,
});

export const workspaceQueryOptions = queryOptions({
  queryKey: qk.workspace,
  queryFn: fetchWorkspace,
  staleTime: 5 * 60_000,
});

export const spacesQueryOptions = queryOptions({
  queryKey: qk.spaces,
  queryFn: fetchSpaces,
  // Inherit the 30s default so the switcher revalidates against ClickUp on
  // mount (a persisted snapshot can otherwise mask a just-added space).
});

export const settingsQueryOptions = queryOptions({
  queryKey: qk.settings,
  queryFn: fetchSettings,
});

export const usersQueryOptions = queryOptions({
  queryKey: qk.users,
  queryFn: fetchUsers,
});

export const membersQueryOptions = queryOptions({
  queryKey: qk.members,
  queryFn: fetchMembers,
});

/** Live count of what a cutoff would archive/restore in a space, keyed by both. */
export const archivePreviewOptions = (
  cutoff: string | null,
  spaceId: string,
) =>
  queryOptions({
    queryKey: [...qk.archivePreview, spaceId, cutoff],
    queryFn: () => fetchArchivePreview(cutoff as string, spaceId),
    enabled: cutoff != null && !!spaceId,
    // Cheap, but no point refetching a fixed date's count on focus.
    staleTime: 30_000,
  });

// ---- Board ------------------------------------------------------------------

export const boardColumnOptions = (
  status: SignoffStatus,
  spaceId: string,
  filters: BoardFilters = EMPTY_FILTERS,
) =>
  infiniteQueryOptions({
    // Space + filters are part of the key, so each space/filter combination
    // caches separately and re-applying a prior view paints instantly. TanStack
    // hashes the key object deterministically, so field order doesn't matter.
    queryKey: [...qk.board, spaceId, status, filters],
    queryFn: ({ pageParam }) =>
      fetchBoardPage(status, BOARD_PAGE_SIZE, pageParam, spaceId, filters),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.tasks.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    // No space resolved yet → don't fetch (the board redirects to a default).
    enabled: !!spaceId,
    // The board reflects server state that changes out-of-band — a scheduled
    // sync, or a sync run from another tab/session. Override the global
    // refetchOnWindowFocus:false here so returning to the tab revalidates, and
    // poll while focused so it stays current without a manual reload.
    // refetchInterval is foreground-only by default, so background tabs idle.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

export const boardFacetsOptions = (spaceId: string) =>
  queryOptions({
    queryKey: [...qk.boardFacets, spaceId],
    queryFn: () => fetchBoardFacets(spaceId),
    staleTime: 5 * 60_000,
    enabled: !!spaceId,
  });

export const archivedInfiniteOptions = (search: string, spaceId: string) =>
  infiniteQueryOptions({
    queryKey: [...qk.archived, spaceId, search],
    queryFn: ({ pageParam }) =>
      fetchArchived(ARCHIVED_PAGE_SIZE, pageParam, search, spaceId),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.tasks.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: !!spaceId,
    // Sync can archive tasks (reconcile) — revalidate the archived list when the
    // tab regains focus so it reflects what just moved here.
    refetchOnWindowFocus: true,
  });

export const taskDetailOptions = (id: string | null) =>
  queryOptions({
    queryKey: [...qk.task, id],
    queryFn: () => fetchTaskDetail(id as string),
    enabled: id != null,
    // An open task can change server-side (a sync, or another reviewer acting).
    // Revalidate on refocus so the modal isn't stale; no interval, to avoid
    // hammering the per-open lazy ClickUp fetch (it already has a 30s cache).
    refetchOnWindowFocus: true,
  });

export const subtaskDescriptionOptions = (clickupId: string | null) =>
  queryOptions({
    queryKey: [...qk.subtaskDesc, clickupId],
    queryFn: () => fetchSubtaskDescription(clickupId as string),
    enabled: clickupId != null,
    staleTime: Infinity,
  });

// ---- Notifications ----------------------------------------------------------

export const notificationsInfiniteOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.notifications,
    queryFn: ({ pageParam }) =>
      fetchNotifications(NOTIFICATIONS_PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined;
      return allPages.reduce((n, p) => n + p.notifications.length, 0);
    },
    // Revisiting the feed within 30s paints from cache instead of refetching.
    staleTime: 30_000,
  });

/** Lightweight preview list for the header bell (first page only). */
export const notificationsPreviewOptions = (limit: number) =>
  queryOptions({
    queryKey: [...qk.notifications, "preview", limit],
    queryFn: () => fetchNotifications(limit, 0),
    // The bell prefetches this on hover; keep it fresh long enough that opening
    // the dropdown paints instantly instead of showing a loading spinner.
    staleTime: 60_000,
  });

export const unreadCountOptions = queryOptions({
  queryKey: qk.unread,
  queryFn: fetchUnreadCount,
  // Poll so new notifications surface without a reload. The staleTime stops
  // window-focus refetches from stacking on top of the interval.
  refetchInterval: 60_000,
  staleTime: 60_000,
});

// ---- Board cache mutation ---------------------------------------------------

/**
 * Optimistically drop a just-decided card from a column so it disappears the
 * instant the modal closes; the post-decision invalidation reconciles the rest.
 *
 * The real key is [...qk.board, spaceId, status, filters], so a partial-key
 * match on [board, status] never matches (spaceId sits where status is expected)
 * — match by predicate on the status SLOT instead, so EVERY space + filter
 * variant of that column is updated.
 */
export function dropTaskFromBoardColumns(
  queryClient: QueryClient,
  status: SignoffStatus,
  id: string,
): void {
  queryClient.setQueriesData<InfiniteData<BoardPage>>(
    {
      predicate: (query) =>
        query.queryKey[0] === qk.board[0] && query.queryKey[2] === status,
    },
    (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((p) => {
              const tasks = p.tasks.filter((t) => t.id !== id);
              return {
                tasks,
                total: p.total - (p.tasks.length - tasks.length),
              };
            }),
          }
        : data,
  );
}

/**
 * Archiving / unarchiving / restoring a task changes the board, the archived
 * list, AND that task's own detail (which decides the actions the modal offers).
 * Invalidate all three so a modal re-opened on the same task never offers an
 * action the server now 409s.
 */
export function invalidateAfterArchiveChange(
  queryClient: QueryClient,
  id: string,
): void {
  void queryClient.invalidateQueries({ queryKey: qk.board });
  void queryClient.invalidateQueries({ queryKey: qk.archived });
  void queryClient.invalidateQueries({ queryKey: [...qk.task, id] });
}

// ---- Auth helpers -----------------------------------------------------------

/** Resolve (and cache) the session for route guards in `beforeLoad`. */
export const ensureSession = (queryClient: QueryClient) =>
  queryClient.ensureQueryData(sessionQueryOptions);

/**
 * The signed-in user inside an authenticated route. The route's `beforeLoad`
 * guarantees the session query is populated and non-null, so this never returns
 * null in practice — it throws if misused outside a guarded route.
 */
export function useUser(): SessionUser {
  const { data } = useQuery(sessionQueryOptions);
  if (!data) {
    throw new Error("useUser() used outside an authenticated route");
  }
  return data;
}
